import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class AuthSessionService {
  private static readonly staticApplicationKey = 'application';
  private static readonly staticPrivilegesKey = 'privileges';
  private static readonly staticUserKey = 'user';
  private static readonly staticUserIdKey = 'user_id';

  private readonly acceptTermsKey = 'accept_terms';
  private readonly accessTokenKey = 'access_token';
  private readonly applicationKey = 'application';
  private readonly backupAccessTokenKey = 'bkp_access_token';
  private readonly menuKey = 'menu';
  private readonly privilegesKey = 'privileges';
  private readonly userIdKey = 'user_id';
  private readonly userKey = 'user';

  static getApplicationSnapshot(): any {
    const application = sessionStorage.getItem(AuthSessionService.staticApplicationKey);
    return application ? JSON.parse(application) : null;
  }

  static getApplicationId(): number {
    return AuthSessionService.getApplicationSnapshot()?.id;
  }

  static getPrivilegesSnapshot(): string {
    return sessionStorage.getItem(AuthSessionService.staticPrivilegesKey);
  }

  static getUserIdSnapshot(): string {
    return sessionStorage.getItem(AuthSessionService.staticUserIdKey);
  }

  static getUserSnapshot(): any {
    const user = sessionStorage.getItem(AuthSessionService.staticUserKey);
    return user ? JSON.parse(user) : null;
  }

  buildHeaders(baseHeaders: { [name: string]: string } = {}): HttpHeaders {
    let headers = new HttpHeaders(baseHeaders);
    const accessToken = this.getAccessToken();
    const application = this.getApplication();

    if (accessToken) {
      headers = headers.set('Authorization', accessToken);
    }

    if (application?.id) {
      headers = headers.set('event', String(application.id));
    }

    return headers;
  }

  clear(): void {
    sessionStorage.clear();
  }

  clearBackupAccessToken(): void {
    sessionStorage.removeItem(this.backupAccessTokenKey);
  }

  getAcceptTerms(): string {
    return sessionStorage.getItem(this.acceptTermsKey);
  }

  getAccessToken(): string {
    return sessionStorage.getItem(this.accessTokenKey);
  }

  getApplication(): any {
    const application = sessionStorage.getItem(this.applicationKey);
    return application ? JSON.parse(application) : null;
  }

  getPrivileges(): string {
    return sessionStorage.getItem(this.privilegesKey);
  }

  getUser(): any {
    const user = sessionStorage.getItem(this.userKey);
    return user ? JSON.parse(user) : null;
  }

  getUserId(): string {
    return sessionStorage.getItem(this.userIdKey);
  }

  hasAccessToken(): boolean {
    return Boolean(this.getAccessToken());
  }

  hasApplication(): boolean {
    const application = this.getApplication();
    return Boolean(application && application !== 0);
  }

  hasBackupAccessToken(): boolean {
    return Boolean(sessionStorage.getItem(this.backupAccessTokenKey));
  }

  setAcceptTerms(acceptTerms: any): void {
    sessionStorage.setItem(this.acceptTermsKey, acceptTerms);
  }

  setAccessToken(accessToken: string): void {
    sessionStorage.setItem(this.accessTokenKey, accessToken);
  }

  setApplication(application: any): void {
    sessionStorage.setItem(this.applicationKey, JSON.stringify(application));
  }

  setBackupAccessToken(accessToken: string): void {
    sessionStorage.setItem(this.backupAccessTokenKey, accessToken);
  }

  setMenu(menu: any): void {
    sessionStorage.setItem(this.menuKey, JSON.stringify(menu));
  }

  setPrivileges(privileges: any): void {
    sessionStorage.setItem(this.privilegesKey, privileges);
  }

  setUser(user: any): void {
    sessionStorage.setItem(this.userKey, JSON.stringify(user));
    if (user?.id) {
      this.setUserId(user.id);
    }
  }

  setUserId(userId: string | number): void {
    sessionStorage.setItem(this.userIdKey, String(userId));
  }
}
